import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { throttle } from 'lodash'
import smoothscroll from 'smoothscroll-polyfill'
import { IProductImage } from '../../types/product'
import trackMkpEvents, { labels } from '../../lib/market-place-track-event'
import Image from '../product-image'
import ScrollButton from './scroll-button'
import style from './index.module.scss'
import {
  IMAGE_SIZES_GALLERY_MAIN,
  IMAGE_SIZES_GALLERY_THUMBNAIL,
} from '@scentregroup/shared/hub-components/cloudinary-image'
import { withIsAboveFoldProvider } from '@scentregroup/shared/hub-components/is-above-fold-provider'

interface IImageGalleryProps {
  images: IProductImage[]
}

const MAX_VISIBLE_IMAGES = 5
const MARGIN = 8 / (MAX_VISIBLE_IMAGES - 1)

const thumnailWidth = (elem: HTMLElement): number =>
  elem.getBoundingClientRect().width / MAX_VISIBLE_IMAGES

const thumbnailHeight = (elem: HTMLElement): number =>
  elem.getBoundingClientRect().height / MAX_VISIBLE_IMAGES

const scrollTo = (
  elem: HTMLElement | null,
  scrollLeft: number,
  scrollTop: number
): void => {
  elem?.scrollTo({
    top: scrollTop,
    left: scrollLeft,
    behavior: 'smooth',
  })
}

// handleScroll is called with a throttle function so it's only called once every 250ms
const handleScroll = (
  elem: HTMLElement | null,
  verticalScroll: boolean,
  positive?: boolean
): void => {
  if (elem) {
    const width = thumnailWidth(elem) + MARGIN
    const height = thumbnailHeight(elem) + MARGIN

    const multiplier = positive ? 1 : -1

    // scroll the image either left or right
    const scrollLeft = verticalScroll ? 0 : elem.scrollLeft + width * multiplier
    const scrollTop = verticalScroll ? elem.scrollTop + height * multiplier : 0

    scrollTo(elem, scrollLeft, scrollTop)
  }
}

const Button: React.FC<
  React.PropsWithChildren<
    React.DetailedHTMLProps<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      HTMLButtonElement
    >
  >
> = props => <button {...props} />

const ImageGallery: React.FunctionComponent<
  React.PropsWithChildren<IImageGalleryProps>
> = ({ images: possiblyMissingImages }) => {
  const images = useMemo(
    () => possiblyMissingImages || [],
    [possiblyMissingImages]
  )
  // prevent extra rerendering by having seperate booleans rather than object of booleans
  const [showLeftScroll, setShowLeftScroll] = useState<boolean>(false)
  const [showRightScroll, setShowRightScroll] = useState<boolean>(
    images.length > MAX_VISIBLE_IMAGES
  )

  const [showTopScroll, setShowTopScroll] = useState<boolean>(false)
  const [showBottomScroll, setShowBottomScroll] = useState<boolean>(
    images.length > MAX_VISIBLE_IMAGES - 1
  )

  const [selectedImage, setImage] = useState<number>(0)

  const thumbnailGallery = useRef<HTMLDivElement>(null)

  const setShowScroll = useCallback((): void => {
    if (images.length < MAX_VISIBLE_IMAGES) {
      setShowTopScroll(false)
      setShowBottomScroll(false)
      return
    }

    const elem = thumbnailGallery.current

    if (elem) {
      const horizontalScrollDistance = elem.scrollLeft
      const right =
        horizontalScrollDistance +
          elem.getBoundingClientRect().width +
          thumnailWidth(elem) <=
        elem.scrollWidth
      const left = horizontalScrollDistance > thumnailWidth(elem)
      setShowLeftScroll(left)
      setShowRightScroll(right)

      const verticalScrollDistance = elem.scrollTop
      let bottom =
        verticalScrollDistance +
          elem.getBoundingClientRect().height +
          thumbnailHeight(elem) <=
        elem.scrollHeight
      const top = verticalScrollDistance > 0
      //use this for image that is equal to MAX_VISIBLE_IMAGES
      if (images.length === MAX_VISIBLE_IMAGES) {
        bottom = !top
      }
      setShowTopScroll(top)
      setShowBottomScroll(bottom)
    }
  }, [images.length])

  useEffect(() => {
    smoothscroll.polyfill()
    setShowScroll()
    setImage(0)
    scrollTo(thumbnailGallery.current, 0, 0)
  }, [images, setShowScroll, setImage])
  return (
    <div className={style.imageGalleryWrapper}>
      <div className={style.imageWrapper}>
        <Image image={images[selectedImage]} sizes={IMAGE_SIZES_GALLERY_MAIN} />
      </div>
      <div className={style.thumbnailsWrapper}>
        <div
          ref={thumbnailGallery}
          className={style.thumbnails}
          onScroll={throttle(setShowScroll, 250)}
        >
          {images.map((image, index) => {
            const ButtonWithFold = withIsAboveFoldProvider(
              Button,
              index < MAX_VISIBLE_IMAGES ? 'unknown' : 'below'
            )
            return (
              image && (
                <ButtonWithFold
                  key={index}
                  aria-label={`Expand product image ${index + 1}`}
                  onClick={() => {
                    setImage(index)
                    trackMkpEvents.clicked(labels.PRODUCT_IMAGES)
                  }}
                >
                  <Image
                    image={image}
                    selected={index === selectedImage}
                    sizes={IMAGE_SIZES_GALLERY_THUMBNAIL}
                  />
                </ButtonWithFold>
              )
            )
          })}
        </div>
        {showLeftScroll && (
          <ScrollButton
            direction="left"
            onClick={() => handleScroll(thumbnailGallery.current, false)}
          />
        )}
        {showRightScroll && (
          <ScrollButton
            direction="right"
            onClick={() => handleScroll(thumbnailGallery.current, false, true)}
          />
        )}
        {showTopScroll && (
          <ScrollButton
            direction="up"
            onClick={() => handleScroll(thumbnailGallery.current, true, false)}
          />
        )}
        {showBottomScroll && (
          <ScrollButton
            direction="down"
            onClick={() => handleScroll(thumbnailGallery.current, true, true)}
          />
        )}
      </div>
    </div>
  )
}

export default ImageGallery
